import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

const shuffle = (clips) => {
  const result = [...clips];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

/** Preload only the next clip's metadata; use the dark hero background while loading. */
const BackgroundVideo = ({ clips }) => {
  const reducedMotion = useReducedMotion();
  const containerRef = useRef(null);
  const currentVideoRef = useRef(null);
  const [cursor, setCursor] = useState(0);
  const [failedSources, setFailedSources] = useState([]);
  const [playingSource, setPlayingSource] = useState('');
  const [inView, setInView] = useState(true);
  const [pageVisible, setPageVisible] = useState(!document.hidden);
  const [saveData] = useState(() => Boolean(navigator.connection?.saveData));
  const sequence = useMemo(() => shuffle(clips), [clips]);
  const available = useMemo(
    () => sequence.filter((clip) => !failedSources.includes(clip.src)),
    [sequence, failedSources]
  );
  const currentIndex = cursor % Math.max(available.length, 1);
  const current = available[currentIndex];
  const next = available.length > 1 ? available[(currentIndex + 1) % available.length] : null;
  const enabled = !reducedMotion && !saveData && available.length > 0;
  const shouldPlay = enabled && inView && pageVisible;

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting));
    if (containerRef.current) observer.observe(containerRef.current);
    const onVisibilityChange = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const video = currentVideoRef.current;
    if (!video || !current) return;
    // A preloaded element is reused on the next rotation, so rewind it each time.
    setPlayingSource('');
    video.currentTime = current.start || 0;
  }, [current]);

  useEffect(() => {
    const video = currentVideoRef.current;
    if (!video) return undefined;
    if (shouldPlay) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
    return () => video.pause();
  }, [current, shouldPlay]);

  const advance = (video) => {
    if (available.length === 1) {
      video.currentTime = current.start || 0;
      if (shouldPlay) video.play().catch(() => {});
    } else {
      setCursor((value) => value + 1);
    }
  };

  return (
    <div className="home-hero-media" ref={containerRef}>
      {enabled && [current, next].filter(Boolean).map((clip) => {
        const isCurrent = clip === current;
        return (
          <video
            key={clip.src}
            ref={isCurrent ? currentVideoRef : null}
            className={`home-hero-video${isCurrent && playingSource === clip.src ? ' is-visible' : ''}`}
            style={clip.fit ? { '--home-video-fit': clip.fit, '--home-video-position': 'center' } : undefined}
            src={clip.src}
            muted
            playsInline
            preload={isCurrent ? 'auto' : 'metadata'}
            aria-hidden="true"
            tabIndex={-1}
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              if (clip.start) video.currentTime = Math.min(clip.start, Math.max(0, video.duration - 0.1));
            }}
            onPlaying={() => {
              if (isCurrent) setPlayingSource(clip.src);
            }}
            onTimeUpdate={(event) => {
              if (isCurrent && shouldPlay && event.currentTarget.currentTime - (clip.start || 0) >= (clip.duration || 9)) {
                advance(event.currentTarget);
              }
            }}
            onEnded={(event) => {
              if (isCurrent) advance(event.currentTarget);
            }}
            onError={() => setFailedSources((sources) => [...new Set([...sources, clip.src])])}
          />
        );
      })}
      <div className="home-hero-shade" aria-hidden="true" />
    </div>
  );
};

export default BackgroundVideo;
